/* eslint-disable react/jsx-props-no-spreading */
import { createPathToRegexpParser } from '@folklore/routes';
import { SocketContainer } from '@folklore/socket';
import { GoogleKeysProvider, EditorProvider } from '@micromag/core/contexts';
import { FieldsProvider as MicromagFieldsProvider } from '@micromag/fields';
import { IntlProvider } from '@micromag/intl';
import { QueryProvider } from '@panneau/data';
import DisplaysProvider from '@panneau/displays';
import FieldsProvider from '@panneau/fields';
import FiltersProvider from '@panneau/filters';
import ActionsProvider from '@panneau/actions';
import ModalsProvider from '@panneau/modals';
import { IntlProvider as PanneauIntlProvider } from '@panneau/intl';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Router } from 'wouter';

import * as AppPropTypes from '../lib/PropTypes';

import * as fields from '../panneau/fields';
import App from './App';

const pathToRegexpParser = createPathToRegexpParser();

const propTypes = {
    locale: PropTypes.string,
    locales: PropTypes.arrayOf(PropTypes.string),
    translations: PropTypes.objectOf(PropTypes.string),
    user: AppPropTypes.user,
    storyLink: AppPropTypes.storyLink,
    organisation: AppPropTypes.organisation,
    publications: AppPropTypes.publicationServices.isRequired,
    routes: AppPropTypes.routes.isRequired,
    domain: PropTypes.string,
    basePath: PropTypes.string,
    pubnubNamespace: PropTypes.string.isRequired,
    pubnubSubscribeKey: PropTypes.string.isRequired,
    googleApiKey: PropTypes.string.isRequired,
    page: AppPropTypes.page.isRequired,
    mediaPipelines: AppPropTypes.mediaPipelines,
};

const defaultProps = {
    locale: 'en',
    locales: ['fr', 'en'],
    translations: null,
    user: null,
    storyLink: null,
    organisation: null,
    basePath: null,
    domain: 'default',
    mediaPipelines: null,
};

const Container = ({
    locale,
    locales,
    translations,
    user,
    organisation,
    publications,
    storyLink,
    routes,
    basePath,
    domain,
    pubnubNamespace,
    pubnubSubscribeKey,
    googleApiKey,
    page,
    mediaPipelines,
}) => {
    const [localeLoaded, setLocaleLoaded] = useState(false);
    const [panneauLocaleLoaded, setPanneauLocaleLoaded] = useState(false);

    useEffect(() => {
        let canceled = false;
        import(`@micromag/intl/locale/${locale}`).then(() => {
            if (!canceled) {
                setLocaleLoaded(true);
            }
        });
        return () => {
            canceled = true;
        };
    }, [locale, setLocaleLoaded]);

    useEffect(() => {
        let canceled = false;
        import(`@panneau/intl/locale/${locale}`).then(() => {
            if (!canceled) {
                setPanneauLocaleLoaded(true);
            }
        });
        return () => {
            canceled = true;
        };
    }, [locale, setPanneauLocaleLoaded]);

    const { google_api_key: organisationGoogleApiKey = null } = organisation || {};
    const finalGoogleApiKey =
        organisationGoogleApiKey !== null ? organisationGoogleApiKey : googleApiKey;

    return localeLoaded && panneauLocaleLoaded ? (
        <GoogleKeysProvider apiKey={finalGoogleApiKey}>
            <SocketContainer namespace={pubnubNamespace} subscribeKey={pubnubSubscribeKey}>
                <PanneauIntlProvider locale={locale} locales={locales}>
                    <IntlProvider locale={locale} locales={locales} extraMessages={translations}>
                        <Router base={basePath} parser={pathToRegexpParser}>
                            <MicromagFieldsProvider>
                                <QueryProvider>
                                    <FieldsProvider components={fields}>
                                        <DisplaysProvider>
                                            <FiltersProvider>
                                                <ActionsProvider>
                                                    <ModalsProvider>
                                                        <EditorProvider>
                                                            <App
                                                                routes={routes}
                                                                user={user}
                                                                page={page}
                                                                organisation={organisation}
                                                                publications={publications}
                                                                storyLink={storyLink}
                                                                domain={domain}
                                                                mediaPipelines={mediaPipelines}
                                                            />
                                                        </EditorProvider>
                                                    </ModalsProvider>
                                                </ActionsProvider>
                                            </FiltersProvider>
                                        </DisplaysProvider>
                                    </FieldsProvider>
                                </QueryProvider>
                            </MicromagFieldsProvider>
                        </Router>
                    </IntlProvider>
                </PanneauIntlProvider>
            </SocketContainer>
        </GoogleKeysProvider>
    ) : null;
};

Container.propTypes = propTypes;
Container.defaultProps = defaultProps;

export default Container;
